import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, CreditCard, Loader2, LogOut } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { createBillingPortalSession } from '../services/subscriptions';

const PAYMENT_FAILED_STATUSES = ['past_due', 'unpaid'];

// Dedicated screen users get redirected to when their subscription payment
// has failed (see RequireSubscription). Not wrapped in RequireSubscription
// itself, otherwise landing here would immediately bounce right back.
const UpdatePayment = () => {
  const { user, loading, logout, subscriptionStatus, refreshSubscriptionStatus } = useAuth();
  const navigate = useNavigate();
  const [redirecting, setRedirecting] = useState(false);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login', { replace: true });
    }
  }, [user, loading, navigate]);

  // Once the payment issue clears (card updated in the Stripe portal and the
  // webhook/backend catches up), send the user back into the app.
  useEffect(() => {
    if (subscriptionStatus && !PAYMENT_FAILED_STATUSES.includes(subscriptionStatus)) {
      navigate('/dashboard', { replace: true });
    }
  }, [subscriptionStatus, navigate]);

  // Re-check status when the user comes back from the Stripe portal tab.
  useEffect(() => {
    const onFocus = async () => {
      setChecking(true);
      try {
        await refreshSubscriptionStatus();
      } finally {
        setChecking(false);
      }
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refreshSubscriptionStatus]);

  const handleUpdatePaymentMethod = async () => {
    setRedirecting(true);
    try {
      const { portal_url } = await createBillingPortalSession();
      window.location.href = portal_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No se pudo abrir el portal de pagos');
      setRedirecting(false);
    }
  };

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-card/60 backdrop-blur-md border border-border rounded-2xl p-8 text-center">
        <div className="mx-auto w-14 h-14 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mb-4">
          <AlertTriangle className="w-7 h-7 text-red-600 dark:text-red-400" />
        </div>
        <h1 className="text-xl font-bold font-outfit text-foreground mb-2">
          No pudimos cobrar tu suscripción
        </h1>
        <p className="text-muted-foreground mb-6">
          Tu último intento de pago falló. Actualiza tu método de pago para recuperar el acceso a la
          plataforma.
        </p>
        <Button onClick={handleUpdatePaymentMethod} disabled={redirecting} className="w-full">
          {redirecting ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <CreditCard className="w-4 h-4 mr-2" />
          )}
          Actualizar método de pago
        </Button>
        <button
          type="button"
          onClick={logout}
          className="mt-4 text-sm text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
        >
          <LogOut className="w-3.5 h-3.5" />
          Cerrar sesión
        </button>
        {checking && (
          <p className="text-xs text-muted-foreground/70 mt-4">Verificando el estado de tu pago…</p>
        )}
      </div>
    </div>
  );
};

export default UpdatePayment;
